import { useCallback } from 'react';
import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import { estimatePageOnlySeconds } from '../utils/timeEstimate';

type CountComponent = {
  color: string;
  name: string;
  symbol: string;
  referenceImage: string;
  symbolType?: string;
  componentType?: string;
  subscript?: string;
  description?: string;
};

type PageSelectionContext = {
  component: CountComponent;
  componentIndex: number;
  newComponents?: CountComponent[];
  parallelMode?: boolean;
} | null;

interface UseCountAllFlowParams {
  pdfDocument: any;
  numPages: number | null;
  pageIndex?: Array<{ page: number; label: string; sheetName?: string }> | null;
  countSingleComponent: (
    component: CountComponent,
    componentIndex: number,
    pagesToCount?: number[],
    options?: {
      keepCountingState?: boolean;
      lightingGroupComponentsOverride?: CountComponent[];
      teleDataGroupComponentsOverride?: CountComponent[];
      countTargetNamesOverride?: Set<string>;
      timerPageCountOverride?: number;
    }
  ) => Promise<void>;
  countTargetNamesRef: MutableRefObject<Set<string>>;
  powerColor: string;
  teleDataColor: string;
  lightingFixtureColor: string;
  pageSelectionContext: PageSelectionContext;
  selectedPagesForCount: Set<number>;
  countAllComponentProgress: Array<any>;
  currentReviewComponentIndex: number;
  pendingKeyComponents: CountComponent[];
  isParallelCountSession: boolean;
  setIsParallelCountSession: Dispatch<SetStateAction<boolean>>;
  setDetectionClassifications: Dispatch<SetStateAction<Map<string, string>>>;
  setReceptacleGroupComponents: Dispatch<SetStateAction<CountComponent[]>>;
  setTeleDataGroupComponents: Dispatch<SetStateAction<CountComponent[]>>;
  setLightingGroupComponents: Dispatch<SetStateAction<CountComponent[]>>;
  setPendingKeyComponents: Dispatch<SetStateAction<CountComponent[]>>;
  setSelectedPagesForCount: Dispatch<SetStateAction<Set<number>>>;
  setPageSelectionContext: Dispatch<SetStateAction<PageSelectionContext>>;
  setShowPageSelectionModal: Dispatch<SetStateAction<boolean>>;
  setCountAllComponentProgress: Dispatch<SetStateAction<Array<any>>>;
  setMultiComponentDetections: Dispatch<SetStateAction<Array<any>>>;
  setAllComponentSelections: Dispatch<SetStateAction<Map<string, Set<string>>>>;
  setCurrentReviewComponentIndex: Dispatch<SetStateAction<number>>;
  setReceptacleClassFilter: Dispatch<SetStateAction<string>>;
  setShowMultiComponentReviewModal: Dispatch<SetStateAction<boolean>>;
  setIsCountingAll: Dispatch<SetStateAction<boolean>>;
  setCountAllProgress: Dispatch<SetStateAction<string>>;
  setBulkSelectedDetections: Dispatch<SetStateAction<Set<string>>>;
  setBulkReclassTarget: Dispatch<SetStateAction<string>>;
  setComponentPagesByName: Dispatch<SetStateAction<Map<string, number[]>>>;
}

export function useCountAllFlow({
  pdfDocument,
  numPages,
  pageIndex,
  countSingleComponent,
  countTargetNamesRef,
  powerColor,
  teleDataColor,
  lightingFixtureColor,
  pageSelectionContext,
  selectedPagesForCount,
  countAllComponentProgress,
  currentReviewComponentIndex,
  pendingKeyComponents,
  isParallelCountSession,
  setIsParallelCountSession,
  setDetectionClassifications,
  setReceptacleGroupComponents,
  setTeleDataGroupComponents,
  setLightingGroupComponents,
  setPendingKeyComponents,
  setSelectedPagesForCount,
  setPageSelectionContext,
  setShowPageSelectionModal,
  setCountAllComponentProgress,
  setMultiComponentDetections,
  setAllComponentSelections,
  setCurrentReviewComponentIndex,
  setReceptacleClassFilter,
  setShowMultiComponentReviewModal,
  setIsCountingAll,
  setCountAllProgress,
  setBulkSelectedDetections,
  setBulkReclassTarget,
  setComponentPagesByName,
}: UseCountAllFlowParams) {
  const getComponentKey = useCallback((c: any) => {
    if (c.name === '__RECEPTACLES_GROUP__') return '__RECEPTACLES_GROUP__';
    if (c.name === '__LIGHTING_GROUP__') return '__LIGHTING_GROUP__';
    if (c.name === '__TELE_DATA_GROUP__') return '__TELE_DATA_GROUP__';
    if (c.symbolType === 'Lighting') return c.symbol || c.name;
    return c.name;
  }, []);

  const getDisplayName = useCallback((c: any) => {
    if (c.name === '__RECEPTACLES_GROUP__') return 'RECEPTACLES';
    if (c.name === '__LIGHTING_GROUP__') return 'LIGHTING';
    if (c.name === '__TELE_DATA_GROUP__') return 'Tele/data outlets';
    return c.name;
  }, []);

  const getBatchesPerPage = useCallback((c: any) => {
    const n = (c.name || '').toLowerCase();
    const isLighting = c.symbolType === 'Lighting' || c.name === '__LIGHTING_GROUP__';
    const isSwitch = c.componentType === 'Switches' || (n.includes('switch') && !n.includes('receptacle'));
    const isReceptacle = n.includes('receptacle') || n.includes('duplex') || c.componentType === 'Receptacles' || c.name === '__RECEPTACLES_GROUP__';
    const isTeleData =
      c.name === '__TELE_DATA_GROUP__' || n.includes('tel') || n.includes('data') || n.includes('communication') || c.componentType === 'Telecom' || c.componentType === 'Low Voltage';
    return (isLighting || isSwitch || isReceptacle || isTeleData) ? 20 : 5;
  }, []);

  const buildImmediateGroupContext = useCallback((components: CountComponent[]) => {
    const targetNames = new Set<string>();
    const lightingComponents: CountComponent[] = [];
    const teleDataComponents: CountComponent[] = [];

    components.forEach((component) => {
      const name = component.name || '';
      const nameLower = name.toLowerCase();
      targetNames.add(name);
      if (component.symbol) {
        targetNames.add(component.symbol);
        targetNames.add(component.symbol.toUpperCase());
      }

      if (component.symbolType === 'Lighting' && component.name !== '__LIGHTING_GROUP__') {
        lightingComponents.push(component);
      }

      const isTeleDataComponent =
        component.name === '__TELE_DATA_GROUP__' ||
        nameLower.includes('tel') ||
        nameLower.includes('data') ||
        nameLower.includes('communication') ||
        component.componentType === 'Telecom' ||
        component.componentType === 'Low Voltage';
      if (isTeleDataComponent && component.name !== '__TELE_DATA_GROUP__') {
        teleDataComponents.push(component);
      }
    });

    return {
      targetNames,
      lightingComponents,
      teleDataComponents,
    };
  }, []);

  const mergeAndGroupComponents = useCallback((components: CountComponent[]) => {
    const targetSet = new Set<string>();
    components.forEach((c) => {
      targetSet.add(c.name);
      if (c.symbol) {
        targetSet.add(c.symbol);
        targetSet.add(c.symbol.toUpperCase());
      }
    });
    countTargetNamesRef.current = targetSet;

    const isReceptacleComp = (c: { name: string; componentType?: string }) =>
      c.name.toLowerCase().includes('receptacle') || c.componentType === 'Receptacles' || c.componentType === 'Power';
    const receptacleComps = components.filter(isReceptacleComp);

    let mergedComponents = [...components];

    if (receptacleComps.length >= 1) {
      const groupedReceptacle = {
        color: powerColor,
        name: '__RECEPTACLES_GROUP__',
        symbol: 'RECEPTACLES',
        referenceImage: receptacleComps[0].referenceImage,
        symbolType: undefined as string | undefined,
        componentType: 'Receptacles' as string | undefined,
        subscript: undefined as string | undefined,
        description: undefined as string | undefined,
      };
      const firstReceptacleIdx = components.findIndex(isReceptacleComp);
      mergedComponents = [...components].filter((c) => !isReceptacleComp(c));
      mergedComponents.splice(firstReceptacleIdx, 0, groupedReceptacle);
      const standardReceptacleComps = receptacleComps.filter((c: any) => c.isStandardClass);
      setReceptacleGroupComponents(standardReceptacleComps.length > 0 ? standardReceptacleComps : receptacleComps);
    } else {
      setReceptacleGroupComponents([]);
    }

    const isTeleDataComp = (c: { name: string; componentType?: string }) =>
      c.name.toLowerCase().includes('tel') ||
      c.name.toLowerCase().includes('data') ||
      c.name.toLowerCase().includes('communication') ||
      c.componentType === 'Telecom' ||
      c.componentType === 'Low Voltage';
    const teleDataComps = mergedComponents.filter(isTeleDataComp);
    if (teleDataComps.length >= 1) {
      const groupedTeleData = {
        color: teleDataColor,
        name: '__TELE_DATA_GROUP__',
        symbol: 'TELE/DATA',
        referenceImage: teleDataComps[0].referenceImage,
        symbolType: undefined as string | undefined,
        componentType: 'Telecom' as string | undefined,
        subscript: undefined as string | undefined,
        description: undefined as string | undefined,
      };
      const firstTeleDataIdx = mergedComponents.findIndex(isTeleDataComp);
      mergedComponents = mergedComponents.filter((c) => !isTeleDataComp(c));
      mergedComponents.splice(firstTeleDataIdx, 0, groupedTeleData);
      setTeleDataGroupComponents(teleDataComps);
    } else {
      setTeleDataGroupComponents([]);
    }

    const isLightingComp = (c: { symbolType?: string }) => c.symbolType === 'Lighting';
    const lightingComps = mergedComponents.filter(isLightingComp);
    if (lightingComps.length >= 1) {
      const groupedLighting = {
        color: lightingFixtureColor,
        name: '__LIGHTING_GROUP__',
        symbol: '',
        referenceImage: lightingComps[0].referenceImage,
        symbolType: 'Lighting' as string | undefined,
        componentType: 'Lighting' as string | undefined,
        subscript: undefined as string | undefined,
        description: undefined as string | undefined,
      };
      const firstLightingIdx = mergedComponents.findIndex(isLightingComp);
      mergedComponents = mergedComponents.filter((c) => !isLightingComp(c));
      mergedComponents.splice(firstLightingIdx, 0, groupedLighting);
      setLightingGroupComponents(lightingComps);
    } else {
      setLightingGroupComponents([]);
    }

    return mergedComponents;
  }, [
    countTargetNamesRef,
    lightingFixtureColor,
    powerColor,
    teleDataColor,
    setLightingGroupComponents,
    setReceptacleGroupComponents,
    setTeleDataGroupComponents,
  ]);

  const getSmartPageSelection = useCallback(
    (component: { symbolType?: string; componentType?: string; name?: string }): Set<number> => {
      const allPages = new Set(Array.from({ length: numPages || 0 }, (_, i) => i + 1));
      if (!pageIndex || pageIndex.length === 0) return allPages;

      const nameLower = (component.name || '').toLowerCase();
      const symbolType = component.symbolType || '';
      const componentType = (component as any).componentType || '';

      let relevantLabels: string[] = [];

      if (symbolType === 'Lighting') {
        // Prefer reflected ceiling plan pages and include lighting sheets by default.
        relevantLabels = ['reflected ceiling plan', 'lighting'];
      } else if (componentType === 'Switches' || (nameLower.includes('switch') && !nameLower.includes('receptacle'))) {
        relevantLabels = ['lighting', 'reflected ceiling plan', 'power', 'telecom'];
      } else if (componentType === 'Power' || nameLower.includes('receptacle')) {
        relevantLabels = ['power', 'telecom'];
      } else if (nameLower.includes('fire alarm') || nameLower.includes('fire') || componentType === 'Fire Alarm') {
        relevantLabels = ['fire alarm', 'telecom'];
      } else if (
        nameLower.includes('low voltage') ||
        nameLower.includes('telecom') ||
        nameLower.includes('data') ||
        componentType === 'Low Voltage' ||
        componentType === 'Telecom'
      ) {
        relevantLabels = ['low voltage', 'telecom', 'power'];
      } else if (nameLower.includes('panel') || componentType === 'Panel Schedules') {
        relevantLabels = ['panel schedules'];
      } else if (nameLower.includes('demo') || componentType === 'Demolition') {
        relevantLabels = ['demolition'];
      } else if (
        nameLower.includes('junction') ||
        nameLower.includes('jbox') ||
        nameLower.includes('j box') ||
        (nameLower.includes('j') && nameLower.includes('box'))
      ) {
        relevantLabels = ['power', 'lighting', 'reflected ceiling plan', 'telecom'];
      }

      if (relevantLabels.length === 0) return allPages;

      const matchedPages = new Set(
        pageIndex
          .filter((p) => {
            const labelLower = p.label.toLowerCase();
            if (labelLower === 'lighting fixture schedule') return false;
            return relevantLabels.some((label) => labelLower.includes(label));
          })
          .map((p) => p.page)
      );

      if (matchedPages.size === 0) return allPages;
      return matchedPages;
    },
    [numPages, pageIndex]
  );

  const startCountingComponents = useCallback(
    (
      components: Array<{
        color: string;
        name: string;
        symbol: string;
        referenceImage: string;
        symbolType?: string;
        componentType?: string;
        subscript?: string;
        description?: string;
      }>,
      options?: { parallel?: boolean }
    ) => {
      if (!pdfDocument || !numPages || components.length === 0) return;
      const isParallelMode = options?.parallel === true;
      setIsParallelCountSession(isParallelMode);
      setDetectionClassifications(new Map());
      setComponentPagesByName(new Map());
      const mergedComponents = mergeAndGroupComponents(components);

      setPendingKeyComponents(
        mergedComponents.slice(1).map((c) => ({
          color: c.color,
          name: c.name,
          symbol: c.symbol,
          referenceImage: c.referenceImage,
          symbolType: c.symbolType,
          componentType: c.componentType,
          subscript: c.subscript,
          description: c.description,
        }))
      );

      setSelectedPagesForCount(getSmartPageSelection(mergedComponents[0]));
      setPageSelectionContext({
        component: mergedComponents[0],
        componentIndex: 0,
        newComponents: mergedComponents,
        parallelMode: isParallelMode,
      });
      setShowPageSelectionModal(true);
    },
    [
      getSmartPageSelection,
      mergeAndGroupComponents,
      numPages,
      pdfDocument,
      setDetectionClassifications,
      setIsParallelCountSession,
      setPageSelectionContext,
      setPendingKeyComponents,
      setComponentPagesByName,
      setSelectedPagesForCount,
      setShowPageSelectionModal,
    ]
  );

  const startParallelCountingWithPageResolver = useCallback(
    (
      components: CountComponent[],
      resolvePagesForComponent: (component: CountComponent) => Set<number> | number[] | null | undefined
    ) => {
      if (!pdfDocument || !numPages || components.length === 0) return;
      setIsParallelCountSession(true);
      setDetectionClassifications(new Map());
      const immediateGroupContext = buildImmediateGroupContext(components);
      const mergedComponents = mergeAndGroupComponents(components);
      if (mergedComponents.length === 0) return;

      const normalizePages = (component: CountComponent) => {
        const resolved = resolvePagesForComponent(component);
        if (resolved == null) {
          return Array.from(getSmartPageSelection(component)).sort((a, b) => a - b);
        }
        const pages = Array.isArray(resolved)
          ? resolved
          : resolved instanceof Set
          ? Array.from(resolved)
          : [];
        const cleaned = Array.from(new Set(pages.map((p) => Math.floor(Number(p))).filter((p) => Number.isFinite(p) && p >= 1 && p <= (numPages || 1)))).sort(
          (a, b) => a - b
        );
        return cleaned;
      };

      const pageMap = mergedComponents.map((component) => normalizePages(component));
      const combinedSessionPages = new Set<number>();
      pageMap.forEach((pages) => {
        pages.forEach((p) => combinedSessionPages.add(p));
      });
      const combinedSessionPageCount = combinedSessionPages.size;
      setComponentPagesByName(
        new Map(mergedComponents.map((component, idx) => [getComponentKey(component), pageMap[idx]]))
      );
      const nowMs = Date.now();
      const initialProgress = mergedComponents.map((component, idx) => {
        const pagesForComponent = pageMap[idx];
        const batchesPerPage = getBatchesPerPage(component);
        const isSkipped = pagesForComponent.length === 0;
        return {
          name: getDisplayName(component),
          color: component.color,
          referenceImage: component.referenceImage,
          status: isSkipped ? 'complete' : 'processing',
          count: 0,
          completedBatches: 0,
          totalBatches: pagesForComponent.length * batchesPerPage,
          currentPage: 0,
          currentBatch: 0,
          estimatedTotalSeconds: isSkipped ? 0 : estimatePageOnlySeconds(combinedSessionPageCount),
          startedAt: isSkipped ? 0 : nowMs,
        };
      });

      const allComponentResults = mergedComponents.map((component) => ({
        componentName: getComponentKey(component),
        componentColor: component.color,
        referenceImage: component.referenceImage,
        detections: [] as Array<{ id: string; thumbnail: string; prediction: any }>,
        isLighting: component.name === '__LIGHTING_GROUP__' || component.symbolType === 'Lighting',
      }));
      const initialSelections = new Map<string, Set<string>>(
        mergedComponents.map((component) => [getComponentKey(component), new Set<string>()])
      );

      setShowPageSelectionModal(false);
      setPageSelectionContext(null);
      setPendingKeyComponents([]);
      setCountAllComponentProgress(initialProgress);
      setMultiComponentDetections(allComponentResults);
      setAllComponentSelections(initialSelections);
      setCurrentReviewComponentIndex(0);
      setReceptacleClassFilter('all');
      setShowMultiComponentReviewModal(true);
      setIsCountingAll(true);
      setCountAllProgress(`Counting ${mergedComponents.length} component groups across selected pages...`);

      void (async () => {
        await new Promise((resolve) => setTimeout(resolve, 50));
        try {
          const runnableComponents = mergedComponents
            .map((component, idx) => ({ component, idx, pages: pageMap[idx] }))
            .filter((entry) => entry.pages.length > 0);
          await Promise.all(
            runnableComponents.map((entry) =>
              countSingleComponent(entry.component, entry.idx, entry.pages, {
                keepCountingState: true,
                lightingGroupComponentsOverride: immediateGroupContext.lightingComponents,
                teleDataGroupComponentsOverride: immediateGroupContext.teleDataComponents,
                countTargetNamesOverride: immediateGroupContext.targetNames,
                timerPageCountOverride: combinedSessionPageCount,
              })
            )
          );
        } catch (error) {
          console.error('❌ Error in guided parallel counting flow:', error);
        } finally {
          setIsCountingAll(false);
          setCountAllProgress('');
        }
      })();
    },
    [
      buildImmediateGroupContext,
      countSingleComponent,
      getBatchesPerPage,
      getComponentKey,
      getDisplayName,
      getSmartPageSelection,
      mergeAndGroupComponents,
      numPages,
      pdfDocument,
      setAllComponentSelections,
      setCountAllComponentProgress,
      setCountAllProgress,
      setCurrentReviewComponentIndex,
      setDetectionClassifications,
      setComponentPagesByName,
      setIsCountingAll,
      setIsParallelCountSession,
      setMultiComponentDetections,
      setPageSelectionContext,
      setPendingKeyComponents,
      setReceptacleClassFilter,
      setShowMultiComponentReviewModal,
      setShowPageSelectionModal,
    ]
  );

  const handleConfirmPageSelection = useCallback(async () => {
    const ctx = pageSelectionContext;
    if (!ctx || selectedPagesForCount.size === 0) return;
    const pagesArray = Array.from(selectedPagesForCount).sort((a, b) => a - b);
    const { component, componentIndex, newComponents, parallelMode } = ctx;
    const isParallelMode = !!parallelMode;
    const isLightingComp = component.symbolType === 'Lighting' || component.name === '__LIGHTING_GROUP__';
    const componentNameLower = (component.name || '').toLowerCase();
    const isReceptacleComp =
      componentNameLower.includes('receptacle') ||
      componentNameLower.includes('duplex') ||
      (component as any).componentType === 'Receptacles' ||
      component.name === '__RECEPTACLES_GROUP__';
    const isSwitchComp = (component as any).componentType === 'Switches' || (componentNameLower.includes('switch') && !isReceptacleComp);
    const isTeleDataComp =
      component.name.toLowerCase().includes('tel') ||
      component.name.toLowerCase().includes('data') ||
      component.name.toLowerCase().includes('communication') ||
      (component as any).componentType === 'Telecom' ||
      (component as any).componentType === 'Low Voltage';
    const isOcrBasedComp = isLightingComp || isSwitchComp || isReceptacleComp || isTeleDataComp;
    const displayName =
      component.name === '__RECEPTACLES_GROUP__'
        ? 'RECEPTACLES'
        : component.name === '__TELE_DATA_GROUP__'
        ? 'Tele/data outlets'
        : component.name === '__LIGHTING_GROUP__'
        ? 'LIGHTING'
        : component.name;
    const batchesPerPage = isOcrBasedComp ? 20 : 5;
    const totalBatchesForSelection = pagesArray.length * batchesPerPage;

    setShowPageSelectionModal(false);
    setPageSelectionContext(null);

    if (newComponents) {
      const getComponentKey = (c: any) => {
        if (c.name === '__RECEPTACLES_GROUP__') return '__RECEPTACLES_GROUP__';
        if (c.name === '__LIGHTING_GROUP__') return '__LIGHTING_GROUP__';
        if (c.name === '__TELE_DATA_GROUP__') return '__TELE_DATA_GROUP__';
        if (c.symbolType === 'Lighting') return c.symbol || c.name;
        return c.name;
      };
      const getDisplayName = (c: any) => {
        if (c.name === '__RECEPTACLES_GROUP__') return 'RECEPTACLES';
        if (c.name === '__LIGHTING_GROUP__') return 'LIGHTING';
        if (c.name === '__TELE_DATA_GROUP__') return 'Tele/data outlets';
        return c.name;
      };
      const getTotalBatchesForComponent = (c: any) => {
        const n = (c.name || '').toLowerCase();
        const isLighting = c.symbolType === 'Lighting' || c.name === '__LIGHTING_GROUP__';
        const isSwitch = c.componentType === 'Switches' || (n.includes('switch') && !n.includes('receptacle'));
        const isReceptacle = n.includes('receptacle') || n.includes('duplex') || c.componentType === 'Receptacles' || c.name === '__RECEPTACLES_GROUP__';
        const isTeleData =
          c.name === '__TELE_DATA_GROUP__' || n.includes('tel') || n.includes('data') || n.includes('communication') || c.componentType === 'Telecom' || c.componentType === 'Low Voltage';
        const isOcrBased = isLighting || isSwitch || isReceptacle || isTeleData;
        return pagesArray.length * (isOcrBased ? 20 : 5);
      };
      const nowMs = Date.now();
      const initialProgress = newComponents.map((c, idx) => {
        const isStarting = isParallelMode || idx === 0;
        return {
          name: getDisplayName(c),
          color: c.color,
          referenceImage: c.referenceImage,
          status: isStarting ? ('processing' as const) : ('pending' as const),
          count: 0,
          completedBatches: 0,
          totalBatches: getTotalBatchesForComponent(c),
          currentPage: 0,
          currentBatch: 0,
          estimatedTotalSeconds: estimatePageOnlySeconds(pagesArray.length),
          startedAt: isStarting ? nowMs : 0,
        };
      });
      const allComponentResults = newComponents.map((c) => ({
        componentName: getComponentKey(c),
        componentColor: c.color,
        referenceImage: c.referenceImage,
        detections: [] as Array<{ id: string; thumbnail: string; prediction: any }>,
        isLighting: c.name === '__LIGHTING_GROUP__' || c.symbolType === 'Lighting',
      }));
      const initialSelections = new Map<string, Set<string>>(newComponents.map((c) => [getComponentKey(c), new Set<string>()]));
      setComponentPagesByName(new Map(newComponents.map((c) => [getComponentKey(c), pagesArray])));
      setCountAllComponentProgress(initialProgress);
      setMultiComponentDetections(allComponentResults);
      setAllComponentSelections(initialSelections);
      setCurrentReviewComponentIndex(0);
      setReceptacleClassFilter('all');
      setShowMultiComponentReviewModal(true);

      setIsCountingAll(true);
      setCountAllProgress(
        isParallelMode
          ? `Counting ${newComponents.length} component groups across ${pagesArray.length} pages...`
          : `Counting "${displayName}" across ${pagesArray.length} pages...`
      );
      if (isParallelMode) {
        setPendingKeyComponents([]);
      }
    } else {
      const selectedKey =
        component.name === '__RECEPTACLES_GROUP__'
          ? '__RECEPTACLES_GROUP__'
          : component.name === '__LIGHTING_GROUP__'
            ? '__LIGHTING_GROUP__'
            : component.name === '__TELE_DATA_GROUP__'
              ? '__TELE_DATA_GROUP__'
              : component.symbolType === 'Lighting'
                ? component.symbol || component.name
                : component.name;
      setComponentPagesByName((prev) => {
        const next = new Map(prev);
        next.set(selectedKey, pagesArray);
        return next;
      });
      setPendingKeyComponents((prev) => prev.slice(1));
      setCountAllComponentProgress((prev) =>
        prev.map((p, i) => (i === componentIndex ? { ...p, status: 'processing' as const, totalBatches: totalBatchesForSelection, startedAt: Date.now(), estimatedTotalSeconds: estimatePageOnlySeconds(pagesArray.length) } : p))
      );
      setIsCountingAll(true);
      setCountAllProgress(`Counting "${displayName}" across ${pagesArray.length} pages...`);
    }

    await new Promise((resolve) => setTimeout(resolve, 50));
    if (newComponents && isParallelMode) {
      void (async () => {
        try {
          await Promise.all(newComponents.map((comp, idx) => countSingleComponent(comp, idx, pagesArray, { keepCountingState: true, timerPageCountOverride: pagesArray.length })));
        } catch (error) {
          console.error('❌ Error in parallel power counting flow:', error);
        } finally {
          setIsCountingAll(false);
          setCountAllProgress('');
        }
      })();
      return;
    }

    try {
      await countSingleComponent(component, componentIndex, pagesArray);
    } catch (error) {
      console.error(`❌ Error counting "${displayName}":`, error);
      setIsCountingAll(false);
      setCountAllProgress('');
    }
  }, [
    countSingleComponent,
    pageSelectionContext,
    selectedPagesForCount,
    setAllComponentSelections,
    setCountAllComponentProgress,
    setCountAllProgress,
    setCurrentReviewComponentIndex,
    setIsCountingAll,
    setMultiComponentDetections,
    setPageSelectionContext,
    setPendingKeyComponents,
    setReceptacleClassFilter,
    setComponentPagesByName,
    setShowMultiComponentReviewModal,
    setShowPageSelectionModal,
  ]);

  const handleMultiComponentNext = useCallback(() => {
    const currentProgress = countAllComponentProgress[currentReviewComponentIndex];
    if (currentProgress?.status === 'processing') return;
    const nextIndex = currentReviewComponentIndex + 1;
    setCurrentReviewComponentIndex(nextIndex);
    setBulkSelectedDetections(new Set());
    setBulkReclassTarget('');

    if (!isParallelCountSession && pendingKeyComponents.length > 0 && numPages) {
      const nextComponent = pendingKeyComponents[0];
      setSelectedPagesForCount(getSmartPageSelection(nextComponent));
      setPageSelectionContext({ component: nextComponent, componentIndex: nextIndex });
      setShowPageSelectionModal(true);
    }
  }, [
    countAllComponentProgress,
    currentReviewComponentIndex,
    getSmartPageSelection,
    isParallelCountSession,
    numPages,
    pendingKeyComponents,
    setBulkReclassTarget,
    setBulkSelectedDetections,
    setCurrentReviewComponentIndex,
    setPageSelectionContext,
    setSelectedPagesForCount,
    setShowPageSelectionModal,
  ]);

  const handleMultiComponentBack = useCallback(() => {
    setCurrentReviewComponentIndex((prev) => prev - 1);
    setBulkSelectedDetections(new Set());
    setBulkReclassTarget('');
  }, [setBulkReclassTarget, setBulkSelectedDetections, setCurrentReviewComponentIndex]);

  const toggleMultiDetectionSelection = useCallback(
    (componentName: string, detectionId: string) => {
      setAllComponentSelections((prev) => {
        const newSelections = new Map(prev);
        const componentSelections = new Set(newSelections.get(componentName) || new Set<string>());
        if (componentSelections.has(detectionId)) {
          componentSelections.delete(detectionId);
        } else {
          componentSelections.add(detectionId);
        }
        newSelections.set(componentName, componentSelections);
        return newSelections;
      });
    },
    [setAllComponentSelections]
  );

  return {
    getSmartPageSelection,
    startCountingComponents,
    startParallelCountingWithPageResolver,
    handleConfirmPageSelection,
    handleMultiComponentNext,
    handleMultiComponentBack,
    toggleMultiDetectionSelection,
  };
}
